import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

import { forwardMailApi, ForwarderTargets } from '../../../services/forwardMailApi';

interface RowEdits {
  to: string;
  cc: string;
  bcc: string;
}

export default function ForwarderTargetsTab() {
  const [rows, setRows] = useState<ForwarderTargets[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, RowEdits>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [savedAt, setSavedAt] = useState<Record<string, number>>({});

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await forwardMailApi.listForwarders();
      setRows(r.forwarders);
      const next: Record<string, RowEdits> = {};
      for (const f of r.forwarders) {
        next[f.function_name] = {
          to: f.to.join(', '),
          cc: f.cc.join(', '),
          bcc: f.bcc.join(', '),
        };
      }
      setEdits(next);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'failed to load forwarders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const isDirty = (fn: string) => {
    const orig = rows.find(r => r.function_name === fn);
    const cur = edits[fn];
    if (!orig || !cur) return false;
    return (
      cur.to !== orig.to.join(', ') ||
      cur.cc !== orig.cc.join(', ') ||
      cur.bcc !== orig.bcc.join(', ')
    );
  };

  const save = async (fn: string) => {
    setSaving(s => ({ ...s, [fn]: true }));
    setError(null);
    try {
      const cur = edits[fn];
      const updated = await forwardMailApi.updateForwarder(fn, {
        to: cur.to,
        cc: cur.cc || null,
        bcc: cur.bcc || null,
      });
      setRows(rs =>
        rs.map(r =>
          r.function_name === fn
            ? { ...r, to: updated.to, cc: updated.cc, bcc: updated.bcc }
            : r,
        ),
      );
      setEdits(es => ({
        ...es,
        [fn]: {
          to: updated.to.join(', '),
          cc: updated.cc.join(', '),
          bcc: updated.bcc.join(', '),
        },
      }));
      setSavedAt(a => ({ ...a, [fn]: Date.now() }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'save failed');
    } finally {
      setSaving(s => ({ ...s, [fn]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Edit the destination addresses each SES inbound-forwarder Lambda
          delivers to. Saving updates the Lambda's env vars immediately —
          subsequent inbound mail uses the new targets.
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={reload}
          disabled={loading}
        >
          Reload
        </Button>
      </Stack>

      {rows.map(r => {
        const dirty = isDirty(r.function_name);
        return (
          <Card key={r.function_name} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{r.label}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
                  >
                    {r.function_name}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    Listens for:
                  </Typography>
                  {r.recipients.map(rec => (
                    <Chip key={rec} size="small" label={rec} />
                  ))}
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Forward To"
                    fullWidth
                    size="small"
                    value={edits[r.function_name]?.to ?? ''}
                    onChange={e =>
                      setEdits(es => ({
                        ...es,
                        [r.function_name]: {
                          ...es[r.function_name],
                          to: e.target.value,
                        },
                      }))
                    }
                    helperText="Comma-separated. At least one required."
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Forward Cc"
                    fullWidth
                    size="small"
                    value={edits[r.function_name]?.cc ?? ''}
                    onChange={e =>
                      setEdits(es => ({
                        ...es,
                        [r.function_name]: {
                          ...es[r.function_name],
                          cc: e.target.value,
                        },
                      }))
                    }
                    helperText="Comma-separated. Optional."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Forward Bcc"
                    fullWidth
                    size="small"
                    value={edits[r.function_name]?.bcc ?? ''}
                    onChange={e =>
                      setEdits(es => ({
                        ...es,
                        [r.function_name]: {
                          ...es[r.function_name],
                          bcc: e.target.value,
                        },
                      }))
                    }
                    helperText="Comma-separated. Optional."
                  />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                {savedAt[r.function_name] &&
                  Date.now() - savedAt[r.function_name] < 5000 && (
                    <Typography variant="caption" color="success.main">
                      Saved
                    </Typography>
                  )}
                <Button
                  variant="contained"
                  startIcon={
                    saving[r.function_name] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={() => save(r.function_name)}
                  disabled={!dirty || saving[r.function_name]}
                >
                  Save
                </Button>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}
